import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import { MeasurementType, type HydrationMeasurement } from "@fitness-app/data-models/entities/Measurement";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { TRAINEE_MEASUREMENTS_REDUCER_NAME } from "../types";

type Payload = {
  data: HydrationMeasurement["data"];
  eventDate: string;
  traineeId: string;
  id?: null | string;
  userId?: string | null;
};

export const upsertHydrationMeasurement = createAsyncThunk<HydrationMeasurement, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_MEASUREMENTS_REDUCER_NAME}/upsertHydrationMeasurement`,
  async (payload, { rejectWithValue, extra: { db, auth, errorTrackingService, analytics } }) => {
    if (payload.id) {
      const { data, error } = await db
        .from("measurement")
        .update({
          data: payload.data,
          updatedAt: new Date().toISOString(),
        })
        .eq("id", payload.id)
        .select("*")
        .single<HydrationMeasurement>();

      if (error) {
        errorTrackingService?.recordError(error, "updateHydrationMeasurement");
        return rejectWithValue(error.message);
      }

      analytics.track("update_hydration_measurement", { data: payload.data });

      return data;
    }

    // Hydration is one row per (traineeId, day). Update-first by that key so a duplicate add
    // (stale client, or a row created by another session) converges onto the existing row rather
    // than inserting a second one and hitting the partial unique index
    // `measurement_daily_type_unique_idx`.
    const updateExistingDay = () =>
      db
        .from("measurement")
        .update({ data: payload.data, updatedAt: new Date().toISOString() })
        .match({
          traineeId: payload.traineeId,
          type: MeasurementType.HydrationMeasurement,
          eventDate: payload.eventDate,
        })
        .select("*")
        .returns<HydrationMeasurement[]>();

    const { data: existing, error: existingError } = await updateExistingDay();

    if (existingError) {
      errorTrackingService?.recordError(existingError, "addHydrationMeasurement");
      return rejectWithValue(existingError.message);
    }

    const [existingRow] = existing ?? [];
    if (existingRow) {
      analytics.track("add_hydration_measurement", { data: payload.data });
      return existingRow;
    }

    // TRE-1706: a client may only insert a `measurement` whose `userId` is their own `auth.uid()`,
    // so a null owner is rejected by RLS. The caller reads the id from `useUserId()`, which returns
    // "" until the session hydrates — resolve the owner from the session instead. Only the insert
    // path needs it; the update paths above are already scoped by an existing row.
    const user = await getLoggedUser(auth);

    const measurement = {
      type: MeasurementType.HydrationMeasurement,
      data: payload.data,
      eventDate: payload.eventDate,
      eventTimestamp: dayjs(payload.eventDate, "YYYY-MM-DD").hour(12).toISOString(),
      traineeId: payload.traineeId,
      creator: AuthorTypeEnum.Client,
      id: generateUniqId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: payload.userId || user.id,
    };

    const { error, data } = await db.from("measurement").insert(measurement).select("*").single<HydrationMeasurement>();

    if (error) {
      // Lost the insert race against a concurrent add: update the now-existing row instead of
      // surfacing the unique-violation.
      if (error.code === "23505") {
        const { data: raced, error: racedError } = await updateExistingDay();
        const [racedRow] = raced ?? [];
        if (!racedError && racedRow) {
          analytics.track("add_hydration_measurement", { data: payload.data });
          return racedRow;
        }
      }
      errorTrackingService?.recordError(error, "addHydrationMeasurement");

      return rejectWithValue(error.message);
    }

    analytics.track("add_hydration_measurement", { data: payload.data });

    return data;
  },
);
